/**
 * Protected Route Component
 * 
 * Wrapper component for admin routes.
 * Uses client-side auth check as defense-in-depth.
 * Cloudflare Access should protect /admin/* routes at the edge.
 */

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { useAdminAuth } from '../../hooks/useAdminAuth';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /** Require admin role (default: true) - not used, kept for API compatibility */
  requireAdmin?: boolean;
}

/**
 * Protected Route Component
 * 
 * Checks authentication status and redirects if not authenticated.
 * This is a client-side check - Cloudflare Access should also protect at the edge.
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated, isLoading, error } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    // If not loading and not authenticated, redirect to home
    if (!isLoading && !isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Show error if auth check failed
  if (error) {
    return (
      <Box sx={{ margin: 2 }}>
        <Alert severity="error">
          Authentication check failed. Please refresh the page.
        </Alert>
      </Box>
    );
  }

  // Only render children if authenticated
  if (!isAuthenticated) {
    return null; // Will redirect via useEffect
  }

  return <>{children}</>;
};

export default ProtectedRoute;

